import { useState, type FormEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  loginAdmin,
  getAdminAuthToken,
  validateAdminToken,
} from "../utils/adminAuth";

// interface LoginProps {
//   onLogin?: (email: string, password: string) => void;
// }

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const navigate = useNavigate();

  // Check if user is already authenticated and redirect to dashboard
  useEffect(() => {
    // console.log("Checking existing admin authentication...");

    async function checkExistingAuth() {

      const token = getAdminAuthToken();
      // console.log("Existing token found:", token);
      if (token) {
        const isValid = await validateAdminToken();
        // console.log("Existing token valid:", isValid);
        if (isValid) {
          navigate("/backoffice");
          return;
        }
      }
      setIsCheckingAuth(false);
    };

    checkExistingAuth();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Simple validation
    if (!email || !password) {
      setError("Por favor, preencha todos os campos");
      setIsLoading(false);
      return;
    }

    try {
      await loginAdmin(email, password);
      // Token is automatically stored in adminAuthToken cookie
      // Redirect to backoffice dashboard
      navigate("/backoffice");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao fazer login");
      setIsLoading(false);
    }
  };

  // Show loading state while checking authentication

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,#1c2135_0,#05060a_55%,#020308_100%)] flex items-center justify-center">
        <div className="text-[#9ca4c0]">
          <svg className="animate-spin h-8 w-8 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#1c2135_0,#05060a_55%,#020308_100%)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-[conic-gradient(from_220deg,#e2b714,#e86fde,#4da9ff,#e2b714)] p-1">
              <div className="w-full h-full rounded-full bg-[#05060a] flex items-center justify-center text-2xl font-bold tracking-wide text-[#f7f7f7]">
                TV
              </div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-[#f7f7f7] mb-1">
            Tarot Virtual
          </h1>
          <p className="text-[#9ca4c0] text-sm">Backoffice · Acesso restrito</p>
        </div>

        {/* Login Form */}
        <div className="bg-gradient-to-br from-[rgba(21,24,35,0.96)] to-[rgba(8,9,17,0.98)] border border-[#262b40] rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-[#f7f7f7] mb-6">
            Iniciar sessão
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-[#9ca4c0] mb-1.5"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[rgba(10,11,20,0.9)] rounded-lg border border-[#262b40] px-3.5 py-2.5 text-[#f7f7f7] text-sm outline-none focus:border-[#e2b714] transition-colors placeholder:text-[#6c7390]"
                placeholder="seu@email.com"
                autoComplete="email"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[#9ca4c0] mb-1.5"
              >
                Palavra-passe
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[rgba(10,11,20,0.9)] rounded-lg border border-[#262b40] px-3.5 py-2.5 text-[#f7f7f7] text-sm outline-none focus:border-[#e2b714] transition-colors placeholder:text-[#6c7390]"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div className="bg-[rgba(255,77,79,0.1)] border border-[rgba(255,77,79,0.3)] rounded-lg px-3 py-2 text-sm text-[#ff4d4f]">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-br from-[#e2b714] to-[#e86fde] text-[#05060a] font-semibold py-2.5 px-4 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "A entrar..." : "Entrar"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#262b40]">
            <p className="text-xs text-[#9ca4c0] text-center">
              Esqueceu a palavra-passe?{" "}
              <a href="#" className="text-[#e2b714] hover:underline">
                Recuperar acesso
              </a>
            </p>
          </div>
        </div>

        {/* Powered by */}
        <div className="mt-6 text-center">
          <p className="text-xs text-[#9ca4c0]">Tarot Virtual © 2025</p>
        </div>
      </div>
    </div>
  );
}
